<template>
  <CModal
    :show.sync="isVisible"
    :close-on-backdrop="false"
    size="xl"
    @update:show="handleClose"
  >
    <template #header>
      <h5 class="modal-title">
        <span class="mr-2">🤖</span>
        <translate translate-context="collaborators.virtualusers">Create Virtual Users</translate>
      </h5>
      <button type="button" class="close" @click="close">
        <span>&times;</span>
      </button>
    </template>

    <div class="px-3 py-2">
      <!-- Description -->
      <div class="alert alert-warning" role="alert">
        <strong><translate translate-context="collaborators.virtualusers">Virtual Users</translate></strong>
        <br>
        <translate translate-context="collaborators.virtualusers">Virtual users are accounts without a real email address. Credentials will be automatically generated and displayed after creation.</translate>
      </div>

      <!-- Username field -->
      <div class="mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="font-weight-bold mb-0">
            <translate translate-context="collaborators.virtualusers">Usernames</translate>
          </label>
          <span v-if="usernames.length > 0" class="badge badge-info">
            {{ usernames.length }} <translate translate-context="collaborators.virtualusers">user(s)</translate>
          </span>
        </div>

        <CInput
          ref="usernameInput"
          v-model="currentInput"
          :placeholder="$pgettext('collaborators.virtualusers', 'e.g., photographer (press Enter to add)')"
          maxlength="50"
          pattern="[a-zA-Z0-9_.]+"
          @keydown.enter.prevent="handleAddUsername"
        />

        <div v-if="usernameError" class="text-danger small mt-1">
          {{ usernameError }}
        </div>

        <!-- Usernames List (badges style) -->
        <div v-if="usernames.length > 0" class="mt-3">
          <div class="d-flex flex-wrap gap-2">
            <span
              v-for="(username, index) in usernames"
              :key="index"
              class="badge badge-info px-3 py-2"
              style="font-size: 14px; cursor: pointer;"
              @click="removeUsername(index)"
            >
              {{ username }} <CIcon name="cilX" size="sm" class="ml-1"/>
            </span>
          </div>
        </div>
      </div>

      <!-- Roles Selection -->
      <div class="mb-3">
        <label class="font-weight-bold">
          <translate translate-context="collaborators.virtualusers">Roles</translate>
        </label>
        <RoleGrid
          v-model="selectedRoles"
          :roles="availableRoles"
          @show-permissions="handleShowPermissions"
        />
      </div>
    </div>

    <template #footer>
      <CButton color="light" @click="close">
        <translate translate-context="collaborators.virtualusers">Cancel</translate>
      </CButton>
      <CButton color="info" @click="handleSubmit" :disabled="!canSubmit">
        <span class="mr-1">🤖</span>
        <translate translate-context="collaborators.virtualusers">Create Virtual Users</translate>
      </CButton>
    </template>

    <RolePermissionsModal
      ref="rolePermissionsModal"
      :scope="scope"
    />
  </CModal>
</template>

<script>
import {mapActions} from 'vuex'
import RoleGrid from './RoleGrid.vue'
import RolePermissionsModal from './RolePermissionsModal.vue'

export default {
  name: 'VirtualUserModal',

  components: {
    RoleGrid,
    RolePermissionsModal
  },

  props: {
    scope: {
      type: Object,
      required: true
    },
    availableRoles: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      isVisible: false,
      usernames: [],
      currentInput: '',
      selectedRoles: {},
      usernameError: ''
    }
  },

  computed: {
    canSubmit() {
      const hasUsernames = this.usernames.length > 0
      const hasRoles = Object.values(this.selectedRoles).some(v => v)
      return hasUsernames && hasRoles
    }
  },

  methods: {
    ...mapActions('collaborators', [
      'createVirtualUsers',
      'fetchCollaborators',
      'fetchRolePermissions'
    ]),

    open() {
      this.isVisible = true
      this.resetForm()
      this.$nextTick(() => {
        this.$refs.usernameInput?.$el?.focus()
      })
    },

    close() {
      this.isVisible = false
    },

    handleClose(val) {
      if (!val) {
        this.close()
      }
    },

    resetForm() {
      this.usernames = []
      this.currentInput = ''
      this.selectedRoles = {}
      this.usernameError = ''
    },

    handleAddUsername() {
      const username = this.currentInput.trim()
      this.usernameError = ''

      if (!username) return

      // Validate username (alphanumeric + underscore + dot)
      const usernameRegex = /^[a-zA-Z0-9_.]+$/
      if (!usernameRegex.test(username)) {
        this.usernameError = this.$pgettext('collaborators.virtualusers', 'Only letters, numbers, underscore and dot are allowed')
        return
      }

      // Check duplicates
      if (this.usernames.includes(username)) {
        this.usernameError = this.$pgettext('collaborators.virtualusers', 'Username already added')
        return
      }

      this.usernames.push(username)
      this.currentInput = ''
    },

    removeUsername(index) {
      this.usernames.splice(index, 1)
    },

    async handleShowPermissions(role) {
      const permissions = await this.fetchRolePermissions(role.id)
      this.$refs.rolePermissionsModal.show(permissions, role)
    },

    async handleSubmit() {
      if (!this.canSubmit) return

      const roleIds = Object.keys(this.selectedRoles)
        .filter(id => this.selectedRoles[id])
        .map(id => ({id: parseInt(id)}))

      try {
        const result = await this.createVirtualUsers({
          users: this.usernames,
          roles: roleIds
        })

        // Handle response: {collaborators: [...], credentials: [...], errors: {...}, success_count: N, error_count: N}
        if (result.success_count > 0) {
          const successMsg = this.$pgettext('collaborators.virtualusers', '{count} virtual user(s) created successfully.')
            .replace('{count}', result.success_count)
          await this.notifySuccess(successMsg)

          // Emit event with credentials to show in modal
          this.$emit('virtual-users-created', result.credentials)
        }

        // Show errors if any
        if (result.error_count > 0 && result.errors && Object.keys(result.errors).length > 0) {
          const errorMessages = Object.entries(result.errors)
            .map(([username, error]) => `${username}: ${error}`)
            .join('\n')
          await this.notifyError(
            this.$pgettext('collaborators.notification', 'Some operations failed:\n') + errorMessages
          )
        }

        this.close()
        await this.fetchCollaborators()
      } catch (error) {
        await this.notifyError(this.$pgettext('collaborators.notification', 'Operation failed'))
      }
    }
  }
}
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
